import React, { useState, useCallback } from 'react';

import Button from 'react-uwp/Button';
import Icon from 'react-uwp/Icon';
import TextBox from 'react-uwp/TextBox';
import Dropdown from 'components/form/Dropdown/Dropdown';
import CustomScrollbar from 'components/CustomScrollbar/CustomScrollbar';

import { maskTypingCPF, maskTypingCNPJ, maskTypingCEP, maskTypingPhone } from 'utils/masks';
import { checkCPF, checkCNPJ } from 'utils/compare';
import metrics from '../../../../../../utils/metrics';

import './ConfigsPaymentData.styles.scss';

const cpfRegex = /^\d{0,11}$/;
const cnpjRegex = /^\d{0,14}$/;
const cepRegex = /^\d{0,8}$/;
const dddRegex = /^\d{0,2}$/;
const phoneRegex = /^\d{0,9}$/;

const cpfFullRegex = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
const cnpjFullRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;
const cepFullRegex = /^\d{5}-\d{3}$/;
const phoneFullRegex = /^\d{4,5}-\d{4}$/;

const stateOptions = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

export default props => {
  const { values, single, error, onReturn, onFinish } = props;

  const [type, setType] = useState(values && values.cpf && values.cpf.length > 14 ? 'PJ' : 'PF');
  const [name, setName] = useState((values && values.name) || null);
  const [cpf, setCpf] = useState((values && values.cpf) || null);
  const [address, setAddress] = useState((values && values.address) || null);
  const [streetNumber, setStreetNumber] = useState((values && values.streetNumber) || null);
  const [complementary, setComplementary] = useState((values && values.complementary) || null);
  const [neighborhood, setNeighborhood] = useState((values && values.neighborhood) || null);
  const [city, setCity] = useState((values && values.city) || null);
  const [state, setState] = useState((values && values.state) || null);
  const [phoneDdd, setPhoneDdd] = useState((values && values.phoneDdd) || null);
  const [phoneNumber, setPhoneNumber] = useState((values && values.phoneNumber) || null);
  const [cep, setCep] = useState((values && values.cep) || null);
  const [bornAtDay, setBornAtDay] = useState((values && values.bornAtDay) || null);
  const [bornAtMonth, setBornAtMonth] = useState((values && values.bornAtMonth) || null);
  const [bornAtYear, setBornAtYear] = useState((values && values.bornAtYear) || null);

  const [errorCPF, setErrorCPF] = useState(false);
  const [cepValidated, setCepValidated] = useState(!!(values && values.cep && cepFullRegex.test(values.cep)));
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState(false);

  const _setCpf = useCallback(
    cpf => {
      const _cpf = cpf
        .replace(/\./g, '')
        .replace(/-/g, '')
        .replace(/\//g, '');

      if (type === 'PF') {
        if (_cpf.length > 11) return;

        if (_cpf.match(cpfRegex)) {
          setCpf(maskTypingCPF(_cpf));
        }

        if (_cpf.length >= 11 && !checkCPF(_cpf)) {
          setErrorCPF(true);
        } else {
          setErrorCPF(false);
        }
      } else if (type === 'PJ') {
        if (_cpf.length > 14) return;

        if (_cpf.match(cnpjRegex)) {
          setCpf(maskTypingCNPJ(_cpf));
        }

        if (_cpf.length >= 14 && !checkCNPJ(_cpf)) {
          setErrorCPF(true);
        } else {
          setErrorCPF(false);
        }
      }
    },
    [setCpf, type],
  );

  const _setCep = useCallback(async cepInput => {
    const _cep = cepInput.replace(/-/g, '');

    if (!_cep.match(cepRegex)) return;

    const masked = maskTypingCEP(_cep);
    setCep(masked);
    setCepValidated(false);
    setCepError(false);

    if (!masked.match(cepFullRegex)) return;

    setCepLoading(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${_cep}/json/`);
      const data = await response.json();

      if (data.erro) {
        setCepError(true);
      } else {
        setAddress(data.logradouro || '');
        setNeighborhood(data.bairro || '');
        setCity(data.localidade || '');
        const stateOption = stateOptions.find(s => s.value === data.uf);
        if (stateOption) setState(stateOption);
        setCepValidated(true);
      }
    } catch {
      setCepError(true);
    } finally {
      setCepLoading(false);
    }
  }, []);

  const _setPhoneDdd = useCallback(
    ddd => {
      if (ddd.match(dddRegex)) {
        setPhoneDdd(ddd);
      }
    },
    [setPhoneDdd],
  );

  const _setPhoneNumber = useCallback(
    number => {
      const _number = number.replace(/-/g, '');

      if (_number.match(phoneRegex)) {
        setPhoneNumber(maskTypingPhone(_number));
      }
    },
    [setPhoneNumber],
  );

  const typeItems = [
    { value: 'PF', label: 'Pessoa Física' },
    { value: 'PJ', label: 'Pessoa Jurídica' },
  ];

  const addressDisabled = !cepValidated || cepLoading;

  return (
    <div className="configs__base">
      <div className="configs__title">
        {!single && (
          <Icon onClick={() => onReturn()} className="configs__title__action">
            ChevronLeft
          </Icon>
        )}
        DADOS PARA A NOTA FISCAL
      </div>
      <CustomScrollbar className="configs__scroll">
        <div className="configs__container">
          <div className="configs__container__session">
            {error === 'INVALID_ZIPCODE' && <div className="configs__payment-data__alert">CEP inválido</div>}

            <div className="configs__payment-data__formItem configs__payment-data__tipo">
              <label>Tipo:</label>
              <Dropdown
                options={typeItems}
                onChange={value => {
                  setType(value.value);
                  if (value.value === 'PJ') {
                    setName(null);
                  }
                  setCpf(null);
                  setErrorCPF(null);
                }}
                placeholder=""
                value={typeItems.find(item => item.value === type)}
                className="configs__payment-data__formItem--born-at-day__field"
              />
            </div>
            <div className="configs__payment-data__formItem">
              <label>{type === 'PF' ? 'Nome completo' : 'Razão social'}:</label>
              <TextBox value={name || ''} onChangeValue={value => setName(value)} background="#1e262a" />
            </div>
            <div className="configs__payment-data__formItem">
              <label>{type === 'PF' ? 'CPF' : 'CNPJ'}:</label>
              <TextBox value={cpf || ''} onChangeValue={value => _setCpf(value)} background="#1e262a" />
              {errorCPF && <div className="configs__payment-data__cpf-error">{type === 'PF' ? 'CPF inválido' : 'CNPJ inválido'}</div>}
            </div>

            <div className="configs__payment-data__formItem">
              <label>CEP:</label>
              <TextBox value={cep || ''} onChangeValue={value => _setCep(value)} background="#1e262a" />
              {cepLoading && <div className="configs__payment-data__cep-status">Consultando CEP...</div>}
              {cepError && <div className="configs__payment-data__cep-error">CEP não encontrado. Verifique e tente novamente.</div>}
              {cepValidated && <div className="configs__payment-data__cep-ok">CEP válido</div>}
            </div>

            <div className={`configs__payment-data__address-block${addressDisabled ? ' configs__payment-data__address-block--disabled' : ''}`}>
              <div className="configs__payment-data__formItem configs__payment-data__formItem--address-line">
                <div className="configs__payment-data__formItem--address">
                  <label>Endereço:</label>
                  <TextBox
                    value={address || ''}
                    onChangeValue={value => setAddress(value)}
                    background="#1e262a"
                    className="configs__payment-data__formItem--address__field"
                    disabled={addressDisabled}
                  />
                </div>
                <div className="configs__payment-data__formItem--street-number">
                  <label>Número:</label>
                  <TextBox
                    value={streetNumber || ''}
                    onChangeValue={value => setStreetNumber(value)}
                    background="#1e262a"
                    className="configs__payment-data__formItem--street-number__field"
                    disabled={addressDisabled}
                  />
                </div>
              </div>

              <div className="configs__payment-data__formItem">
                <label>Complemento:</label>
                <TextBox
                  value={complementary || ''}
                  onChangeValue={value => setComplementary(value)}
                  background="#1e262a"
                  disabled={addressDisabled}
                />
              </div>

              <div className="configs__payment-data__formItem">
                <label>Bairro:</label>
                <TextBox value={neighborhood || ''} onChangeValue={value => setNeighborhood(value)} background="#1e262a" disabled={addressDisabled} />
              </div>

              <div className="configs__payment-data__formItem">
                <label>Cidade:</label>
                <TextBox value={city || ''} onChangeValue={value => setCity(value)} background="#1e262a" disabled={addressDisabled} />
              </div>

              <div className="configs__payment-data__formItem">
                <label>Estado:</label>
                <Dropdown
                  options={stateOptions}
                  onChange={value => setState(value)}
                  placeholder=""
                  value={state}
                  className="configs__payment-data__formItem--born-at-day__field"
                  isDisabled={addressDisabled}
                />
              </div>
            </div>

            <div className="configs__payment-data__formItem configs__payment-data__formItem--phone-line">
              <div className="configs__payment-data__formItem--phone-ddd">
                <label>{type === 'PF' ? 'Celular' : 'Telefone'}:</label>
                <TextBox
                  value={phoneDdd || ''}
                  onChangeValue={value => _setPhoneDdd(value)}
                  background="#1e262a"
                  className="configs__payment-data__formItem--phone-ddd__field"
                />
              </div>
              <div className="configs__payment-data__formItem--phone-number">
                <label>&nbsp;</label>
                <TextBox
                  value={phoneNumber || ''}
                  onChangeValue={value => _setPhoneNumber(value)}
                  background="#1e262a"
                  className="configs__payment-data__formItem--phone-number__field"
                />
              </div>
            </div>

            {type === 'PF' && (
              <div className="configs__payment-data__formItem">
                <label>Data de nascimento:</label>
                <div className="configs__payment-data__formItem--born-at-line">
                  <div className="configs__payment-data__formItem--born-at-day">
                    <Dropdown
                      options={[...Array(31).keys()].map(i => ({
                        value: `000000000${i + 1}`.substr(-2),
                        label: `000000000${i + 1}`.substr(-2),
                      }))}
                      onChange={value => setBornAtDay(value)}
                      placeholder=""
                      value={bornAtDay}
                      className="configs__payment-data__formItem--born-at-day__field"
                    />
                  </div>
                  <div className="configs__payment-data__formItem--born-at-month">
                    <Dropdown
                      options={[...Array(12).keys()].map(i => ({
                        value: `000000000${i + 1}`.substr(-2),
                        label: `000000000${i + 1}`.substr(-2),
                      }))}
                      onChange={value => setBornAtMonth(value)}
                      placeholder=""
                      value={bornAtMonth}
                      className="configs__payment-data__formItem--born-at-day__field"
                    />
                  </div>
                  <div className="configs__payment-data__formItem--born-at-year">
                    <Dropdown
                      options={[...Array(100).keys()].map(i => ({
                        value: 2019 - i,
                        label: 2019 - i,
                      }))}
                      onChange={value => setBornAtYear(value)}
                      placeholder=""
                      value={bornAtYear}
                      className="configs__payment-data__formItem--born-at-day__field"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="configs__payment-data__button">
              <Button
                className="user-options__logout__button"
                onClick={() => {
                  onFinish({
                    name,
                    cpf,
                    address,
                    streetNumber,
                    complementary,
                    neighborhood,
                    city,
                    state,
                    cep,
                    bornAtDay,
                    bornAtMonth,
                    bornAtYear,
                    phoneDdd,
                    phoneNumber,
                  });
                  metrics.collect(metrics.events.DRAWER.CONFIGURACOES.ASSINE_JA.INFO_NF);
                }}
                disabled={
                  !cepValidated ||
                  !name ||
                  !cpf ||
                  (type === 'PF' && !cpf.match(cpfFullRegex)) ||
                  (type === 'PJ' && !cpf.match(cnpjFullRegex)) ||
                  errorCPF ||
                  !address ||
                  !streetNumber ||
                  !neighborhood ||
                  !city ||
                  !state ||
                  !cep ||
                  !cep.match(cepFullRegex) ||
                  !bornAtDay ||
                  !bornAtMonth ||
                  !bornAtYear ||
                  !phoneDdd ||
                  !phoneNumber ||
                  !phoneNumber.match(phoneFullRegex)
                }
              >
                {single ? 'Alterar' : 'Próximo'}
              </Button>
            </div>
          </div>
        </div>
      </CustomScrollbar>
    </div>
  );
};
